// ─────────────────────────────────────────────────────────────────────────────
// graphRegistry — node kind and relation kind as DATA.
//
// No switch statement in a shader, no `if (kind === 'agent')` in the renderer.
// A new node kind or a new relation gains a row here and the renderer is
// untouched — the same discipline the palette follows for colour.
//
// SIZES ARE STARTING VALUES from the specification, in CSS-like pixels before
// pixel-ratio and depth scaling. They are deliberately small: the dense look
// comes from node QUANTITY, hierarchy and fine edges, never from inflating
// every point until the galaxy is visible.
//
// The hub/micro split is a DRAW-CALL split, not a visual one. Hubs are the few
// bright structural nodes (City Hall, boroughs, mansions) that stay resolved at
// every distance; micro nodes are the many. Splitting them lets each layer be
// rebuilt, culled and (from G1) picked independently.
// ─────────────────────────────────────────────────────────────────────────────

import { RexyGraphEdgeRecord, RexyGraphNodeKind, RexyGraphRelationKind } from './graphTypes';

export type RexyGraphNodeLayer = 'hub' | 'micro';

/**
 * Which colour rule a kind obeys. The palette owns the actual colours; this
 * says only which of its rules applies. Keeping the two apart is what lets the
 * owner re-rule a colour without touching the taxonomy.
 */
export type RexyGraphColourRole = 'city-hall' | 'domain' | 'agent' | 'system' | 'leaf';

export interface RexyGraphKindVisual {
  /** Base point size in CSS-like pixels, pre pixel-ratio, pre depth scale. */
  size: number;
  layer: RexyGraphNodeLayer;
  colourRole: RexyGraphColourRole;
}

export const NODE_VISUALS: Record<RexyGraphNodeKind, RexyGraphKindVisual> = {
  'city-hall': { size: 22, layer: 'hub', colourRole: 'city-hall' },
  'city-hall-room': { size: 5, layer: 'micro', colourRole: 'system' },
  domain: { size: 15, layer: 'hub', colourRole: 'domain' },
  agent: { size: 10, layer: 'hub', colourRole: 'agent' },
  'memory-cluster': { size: 5, layer: 'micro', colourRole: 'system' },
  'memory-record': { size: 2, layer: 'micro', colourRole: 'leaf' },
  'memory-file': { size: 2, layer: 'micro', colourRole: 'leaf' },
  file: { size: 2, layer: 'micro', colourRole: 'leaf' },
  document: { size: 2, layer: 'micro', colourRole: 'leaf' },
  thread: { size: 1.6, layer: 'micro', colourRole: 'leaf' },
  artifact: { size: 1.6, layer: 'micro', colourRole: 'leaf' },
  citation: { size: 1.6, layer: 'micro', colourRole: 'leaf' },
  tool: { size: 5.5, layer: 'micro', colourRole: 'system' },
  database: { size: 6, layer: 'micro', colourRole: 'system' },
  workflow: { size: 5.5, layer: 'micro', colourRole: 'system' },
  model: { size: 6.5, layer: 'micro', colourRole: 'system' },
  worker: { size: 4, layer: 'micro', colourRole: 'system' },
  service: { size: 4, layer: 'micro', colourRole: 'system' },
  surface: { size: 4, layer: 'micro', colourRole: 'system' },
  evidence: { size: 4, layer: 'micro', colourRole: 'system' },
  unknown: { size: 2, layer: 'micro', colourRole: 'leaf' },
  // G2.1 — APPENDED DELIBERATELY, OUT OF VISUAL ORDER. KIND_ORDER below is
  // Object.keys of this map and becomes the shader's numeric aKind. Inserting
  // this beside 'memory-cluster' where it visually belongs would renumber every
  // kind after it and move the frozen synthetic layout digest. Position here is
  // an encoding detail; size is what carries the hierarchy — 7 puts a memory
  // system above database (6) and model (6.5), and well below agent (10).
  'memory-system': { size: 7, layer: 'micro', colourRole: 'system' },
  // G2.4 — THE CANONICAL KINDS, APPENDED FOR THE SAME REASON. KIND_ORDER is
  // Object.keys of this map and becomes the shader's aKind, so these go at the
  // end: putting 'primary-agent' beside 'agent' where it belongs visually would
  // renumber every later kind and move the frozen synthetic layout digest, which
  // the live rebuild is not allowed to disturb.
  //
  // SIZE CARRIES THE HIERARCHY (brief §D16). Domain (15) remains the largest
  // structural hub; a primary agent matches the legacy agent at 10; a team reads
  // as a grouping just beneath it at 9; a team member sits below its manager at
  // 6.5. Memory stays structurally distinct but subordinate at wide view — an
  // OS or domain store at 7, an agent's or team's namespace at 5.5.
  'os-root': { size: 22, layer: 'hub', colourRole: 'city-hall' },
  'primary-agent': { size: 10, layer: 'hub', colourRole: 'agent' },
  team: { size: 9, layer: 'hub', colourRole: 'agent' },
  'team-member': { size: 6.5, layer: 'micro', colourRole: 'agent' },
  'os-memory': { size: 7, layer: 'micro', colourRole: 'system' },
  'domain-memory': { size: 7, layer: 'micro', colourRole: 'system' },
  'agent-memory': { size: 5.5, layer: 'micro', colourRole: 'system' },
  'team-memory': { size: 5.5, layer: 'micro', colourRole: 'system' },
};

/**
 * A kind the source has never heard of must not crash the renderer and must not
 * silently vanish — it renders as `unknown` and stays selectable.
 */
export function visualForKind(kind: RexyGraphNodeKind): RexyGraphKindVisual {
  return NODE_VISUALS[kind] || NODE_VISUALS.unknown;
}

/** Numeric kind, handed to the shader as aKind. Stable ordering by declaration. */
const KIND_ORDER: RexyGraphNodeKind[] = Object.keys(NODE_VISUALS) as RexyGraphNodeKind[];

export function kindIndex(kind: RexyGraphNodeKind): number {
  const i = KIND_ORDER.indexOf(kind);
  return i < 0 ? KIND_ORDER.indexOf('unknown') : i;
}

const RELATION_ORDER: RexyGraphRelationKind[] = [
  'contains', 'owns', 'reads', 'writes', 'routes', 'reports-to', 'depends-on',
  'calls', 'uses', 'memory-of', 'tool-of', 'model-of', 'surface-of',
  'derived-from', 'evidence-for', 'approved-cross-domain', 'references',
  'related', 'unknown',
];

export function relationIndex(kind: RexyGraphRelationKind): number {
  const i = RELATION_ORDER.indexOf(kind);
  return i < 0 ? RELATION_ORDER.indexOf('unknown') : i;
}

// ── Edge alpha ────────────────────────────────────────────────────────────────
// Alpha by POSITION IN THE HIERARCHY, not by relation name. The specification's
// ladder: the structural spine is the brightest thing in the graph and the leaf
// relationships are nearly invisible until you are among them. Making every
// relation equally bright is exactly how a graph becomes a white hairball.

/**
 * PRESENTATION SCALE, applied after the semantic ladder — the wiring is drawn
 * quieter, it does not mean less.
 *
 * The measurement that forced it: 25k node points against 74k additive lines and
 * 181k sparks, so the connective tissue owned the frame and the graph read as a
 * bright web rather than as luminous intelligence. The relation weights below are
 * untouched; only their light contribution is scaled.
 *
 * It is NOT applied to the selected-route overlay — an explicitly selected
 * relationship must stay plainly visible.
 */
export const BASE_EDGE_ALPHA_SCALE = 0.35;

const EDGE_ALPHA_CITY_HALL_TO_DOMAIN = 0.18;
const EDGE_ALPHA_DOMAIN_TO_AGENT = 0.12;
const EDGE_ALPHA_AGENT_TO_SYSTEM = 0.07;
const EDGE_ALPHA_LEAF_MIN = 0.025;
const EDGE_ALPHA_LEAF_MAX = 0.055;
const EDGE_ALPHA_CROSS_DOMAIN_APPROVED = 0.08;
const EDGE_ALPHA_BACKGROUND_MIN = 0.012;
const EDGE_ALPHA_BACKGROUND_MAX = 0.035;

/**
 * RENDER PRIORITY, 4 (structural spine) down to 1 (leaf chatter).
 *
 * Computed once at buffer-build time and never in RAF. It is what semantic edge
 * LOD samples against: from galaxy distance the owner needs borough structure,
 * not every file reference on the far side of the sphere. Nothing is deleted —
 * all 74,491 relations stay in the store; this decides what is RASTERISED.
 */
export function edgePriority(
  sourceKind: RexyGraphNodeKind,
  targetKind: RexyGraphNodeKind,
  edge: RexyGraphEdgeRecord,
): 1 | 2 | 3 | 4 {
  const a = visualForKind(sourceKind).colourRole;
  const b = visualForKind(targetKind).colourRole;

  if (a === 'city-hall' || b === 'city-hall') return 4;
  if ((a === 'domain' && b === 'agent') || (a === 'agent' && b === 'domain')) return 4;
  if (a === 'domain' || b === 'domain') return 4;

  if (edge.kind === 'approved-cross-domain') return 3;
  if (a === 'agent' || b === 'agent') return 3;

  if (a === 'system' || b === 'system') return 2;
  return 1;
}

/**
 * Base (unselected) alpha for an edge, from the kinds at each end.
 * `unitJitter` is a deterministic [0,1) derived from the edge id by the caller —
 * it spreads the leaf/background bands so a dense field does not read as one
 * flat wash. It is never Math.random.
 */
export function baseEdgeAlpha(
  sourceKind: RexyGraphNodeKind,
  targetKind: RexyGraphNodeKind,
  edge: RexyGraphEdgeRecord,
  unitJitter: number,
): number {
  const a = visualForKind(sourceKind).colourRole;
  const b = visualForKind(targetKind).colourRole;
  const structural =
    a === 'city-hall' || b === 'city-hall' || a === 'domain' || b === 'domain' ||
    a === 'agent' || b === 'agent';

  // CROSSING A DOMAIN IS NOT, BY ITSELF, IMPORTANCE. The first cut returned the
  // cross-domain constant before looking at the ends, so seven thousand
  // record-to-record references rendered at the brightness of a borough spine
  // and additive blending turned the galaxy into a white hairball. The
  // cross-domain band belongs to STRUCTURAL crossings — the major routes between
  // boroughs and mansions. A reference between two files is a background
  // relation that happens to cross a border.
  let semantic: number;
  if (structural && (edge.kind === 'approved-cross-domain' || edge.crossDomain)) {
    semantic = EDGE_ALPHA_CROSS_DOMAIN_APPROVED;
  } else if (a === 'city-hall' || b === 'city-hall') {
    semantic = EDGE_ALPHA_CITY_HALL_TO_DOMAIN;
  } else if ((a === 'domain' && b === 'agent') || (a === 'agent' && b === 'domain')) {
    semantic = EDGE_ALPHA_DOMAIN_TO_AGENT;
  } else if (a === 'agent' || b === 'agent') {
    semantic = EDGE_ALPHA_AGENT_TO_SYSTEM;
  } else if (edge.kind === 'contains' || edge.kind === 'owns' || edge.kind === 'memory-of') {
    semantic = EDGE_ALPHA_LEAF_MIN + (EDGE_ALPHA_LEAF_MAX - EDGE_ALPHA_LEAF_MIN) * unitJitter;
  } else {
    semantic =
      EDGE_ALPHA_BACKGROUND_MIN +
      (EDGE_ALPHA_BACKGROUND_MAX - EDGE_ALPHA_BACKGROUND_MIN) * unitJitter;
  }
  return semantic * BASE_EDGE_ALPHA_SCALE;
}
